import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { setFormField, setHotel } from '../../actions/formActions'
import NestedCheckboxList from '../NestedCheckboxList'
import SelectedValue from '../SelectedValue'
import TransportSelect from './TransportSelect'
import DestinationsFilter from './DestinationsFilter'
import './style.scss'
import HotelsList from '../HotelsList'
import Button from '../Button'
import Loader from '../Loader'
import Alert from '../Alert'

const DestinationsList = ({
    popular,
    selectedDestinations,
    selectedCities = [],
    destinations,
    setFormField,
    setHotel,
    resetTrigger,
    hotel
}) => {
    const [isSearchMode, setIsSearchMode] = useState(false)
    const [filterDestinations, setFilterDestinations] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [hotels, setHotels] = useState([])
    const [selectedHotel, setSelectedHotel] = useState('')
    const { t } = useTranslation()

    // Pobierz miasta z URL i dodaj je do wybranych wartości dla wyświetlenia
    const getSelectedValuesWithCities = () => {
        if (!selectedDestinations || selectedDestinations === '') {
            return ''
        }

        const cityValues = Array.isArray(selectedCities) ? selectedCities : []
        
        if (cityValues.length === 0) {
            return selectedDestinations
        }

        // Jeśli są miasta w URL, dodaj je jako pełne ścieżki i usuń regiony z wyświetlania
        const kierunekValues = selectedDestinations.split(';')
        const citiesWithPaths = []
        const regionsToExclude = new Set()

        kierunekValues.forEach(kierunek => {
            const parts = kierunek.split('.')
            
            // Jeśli kierunek ma 2 poziomy (kraj.region), dodaj miasta jako pełne ścieżki
            if (parts.length === 2) {
                // Oznacz region do wykluczenia z wyświetlania
                regionsToExclude.add(kierunek)
                
                // Dodaj miasta jako pełne ścieżki (kraj.region.miasto)
                cityValues.forEach(city => {
                    citiesWithPaths.push(`${kierunek}.${city}`)
                })
            } else {
                // Jeśli kierunek nie ma 2 poziomów, zostaw go jak jest
                citiesWithPaths.push(kierunek)
            }
        })

        // Usuń duplikaty i zwróć tylko miasta (pełne ścieżki) i wartości które nie są regionami z miastami
        const uniqueValues = [...new Set(citiesWithPaths)]
        return uniqueValues.join(';')
    }

    useEffect(() => {
        // Clear hotels and selection on external reset
        setSelectedHotel('')
        setHotels([])
    }, [resetTrigger])

    // Sync local selection with Redux (URL param or external set)
    useEffect(() => {
        setSelectedHotel(hotel || '')
    }, [hotel])

    const selectItemHandler = (field, nextSelected) => {
        const currentValues = selectedDestinations ? selectedDestinations.split(';') : []
        const parts = nextSelected.split('.')
        const selectedCityValues = Array.isArray(selectedCities) ? selectedCities : []

        if (parts.length === 3 && selectedCityValues.includes(parts[2])) {
            const remainingCities = selectedCityValues.filter(city => city !== parts[2])
            setFormField('cities', remainingCities)

            if (remainingCities.length === 0) {
                const regionKey = parts.slice(0, 2).join('.')
                setFormField(
                    field,
                    currentValues.filter(value => value !== regionKey && value !== nextSelected).join(';')
                )
            }

            return
        }

        const updateSelection = (nextValues) => {
            const nextCities = parts.length === 3
                ? [...new Set([...selectedCityValues, parts[2]])]
                : []

            setFormField(field, [...new Set(nextValues)].join(';'))
            setFormField('cities', nextCities)
        }

        const selectedAncestors = parts
            .slice(0, -1)
            .map((part, index) => parts.slice(0, index + 1).join('.'))
            .filter(value => currentValues.includes(value))
        const descendants = currentValues.filter(item => item.indexOf(nextSelected + '.') === 0)

        if (selectedAncestors.length > 0) {
            const nextValues = currentValues.filter(item =>
                selectedAncestors.indexOf(item) === -1 && item.indexOf(nextSelected + '.') !== 0
            )

            if (nextValues.indexOf(nextSelected) === -1) {
                nextValues.push(nextSelected)
            }

            updateSelection(nextValues)
            return
        }

        if (descendants.length > 0) {
            const withoutDescendants = currentValues.filter(item => item.indexOf(nextSelected + '.') !== 0)
            const nextValues = withoutDescendants.indexOf(nextSelected) === -1
                ? [...withoutDescendants, nextSelected]
                : withoutDescendants.filter(item => item !== nextSelected)

            updateSelection(nextValues)
            return
        }

        const nextValues = currentValues.includes(nextSelected)
            ? currentValues.filter(value => value !== nextSelected)
            : [...currentValues, nextSelected]

        updateSelection(nextValues)
    }

    const selectHotelHandler = (value) => {
        const next = value === selectedHotel ? '' : value
        setSelectedHotel(next)
        setHotel(next)
        // Nie czyścimy listy hoteli przy odznaczeniu, aby zachować wyniki wyszukiwania
    }

    const handleTransportChangeLoading = (loading) => {
        setIsLoading(loading)
        if (loading) {
            // Transport się zmienia – wyczyść listę hoteli i wybór hotelu
            setHotels([])
            setSelectedHotel('')
        }
    }

    const hasDirectionResults = isSearchMode ? (
        filterDestinations && typeof filterDestinations === 'object' && Object.keys(filterDestinations).length > 0
    ) : true

    const noResults = isSearchMode && !isLoading && !hasDirectionResults && hotels.length === 0

    return (
        <>
            <div className="trip-modal__columns">
                <div className="transport-group">
                    <TransportSelect onChangeTransport={handleTransportChangeLoading}/>
                    <DestinationsFilter
                        currentData={destinations}
                        placeholder={t('modal.destinations.filter_placeholder')}
                        toggleFilterMode={setIsSearchMode}
                        onChange={setFilterDestinations}
                        resetTrigger={resetTrigger}
                        onHotelsChange={setHotels}
                        onHotelsLoading={setIsLoading}
                    />
                </div>
            </div>
            {isLoading ? (
                <div className="trip-modal__columns">
                    <Loader />
                </div>
            ) : noResults ? (
                <div className="trip-modal__columns">
                    <div style={{ width: '100%' }}>
                        <Alert variant="info" message={'Brak wyników'} />
                    </div>
                </div>
            ) : (
                <div className="trip-modal__columns trip-modal__columns--2">
                    <div className="trip-modal__col trip-modal__col--order-mobile-2">
                        {isSearchMode ?
                            (
                                <>
                                    {hasDirectionResults && (
                                        <>
                                            <h4 className="trip-modal__title trip-modal__title--small">
                                                {t('modal.destinations.country_list')}
                                            </h4>
                                            <NestedCheckboxList
                                                field='kierunek'
                                                data={filterDestinations}
                                                expanded={true}
                                                uniquePrefix='kp'
                                                selected={selectedDestinations || ''}
                                                onChange={selectItemHandler}
                                            />
                                        </>
                                    )}
                                </>
                            ):
                            (
                                <>
                                    <h4 className="trip-modal__title trip-modal__title--small">
                                        {t('modal.destinations.section_popular')}
                                    </h4>
                                    <NestedCheckboxList
                                        field="kierunek"
                                        data={popular}
                                        uniquePrefix="kp"
                                        selected={selectedDestinations || ''}
                                        onChange={selectItemHandler}
                                    />
                                    <h4 className="trip-modal__title trip-modal__title--small">
                                        {t('modal.destinations.country_list')}
                                    </h4>
                                    <NestedCheckboxList
                                        field="kierunek"
                                        data={destinations}
                                        uniquePrefix="kl"
                                        selected={selectedDestinations || ''}
                                        onChange={selectItemHandler}
                                    />
                                </>
                            )
                        }
                        {hotels.length > 0 && (
                            <>
                                {hasDirectionResults && <hr className="trip-modal__separator" />}
                                <h4 className="trip-modal__title trip-modal__title--small">
                                    {t('modal.destinations.section_hotels')}
                                </h4>
                                <HotelsList
                                    hotels={hotels}
                                    selectedHotel={selectedHotel}
                                    onSelect={selectHotelHandler}
                                />
                            </>
                        )}
                    </div>
                    <div className="trip-modal__col trip-modal__col--order-mobile-1">
                        <SelectedValue
                            header={t('common.selected_header')}
                            selectedValues={getSelectedValuesWithCities()}
                            field="kierunek"
                            onRemove={selectItemHandler}
                        />
                        {selectedHotel !== '' && (
                            <div className="selected-values__content" style={{ marginTop: 8 }}>
                                <Button
                                    variant="remove"
                                    type="button"
                                    label={selectedHotel}
                                    clickHandler={() => {
                                        setSelectedHotel('')
                                        setHotel('')
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

DestinationsList.propTypes = {
    popular: PropTypes.array,
    selectedDestinations: PropTypes.string,
    selectedCities: PropTypes.array,
    destinations: PropTypes.object,
    setFormField: PropTypes.func,
    setTransport: PropTypes.func,
    setHotel: PropTypes.func,
    hotel: PropTypes.string,
}

const mapStateToProps = (state) => ({
    selectedDestinations: state.formReducer.kierunek,
    selectedCities: state.formReducer.cities,
    hotel: state.formReducer.hotel,
})

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setFormField,
            setHotel
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(DestinationsList)
